<script setup lang="ts">
import { useUFPMHistory } from './composables/useUFPMHistory'
import { scoreBandClass } from './lib/scoreDisplay'
import { exportHistoryXlsx } from './lib/ufpmHistoryExport'

const {
  members,
  dateFrom,
  dateTo,
  memberFilter,
  filteredEntries,
  memberOptions,
  todayISO,
  clearDateFilters,
} = useUFPMHistory()

function scoreLabel(val: string | number | undefined, exempt: boolean | undefined): string {
  if (exempt) return 'EXM'
  return val != null && val !== '' ? String(val) : '—'
}

function compositeLabel(val: string | number | undefined): string {
  const n = parseFloat(String(val ?? ''))
  return Number.isFinite(n) ? n.toFixed(1) : String(val ?? '—')
}

function scoreClass(cat: string | undefined): string {
  const c = (cat || '').toUpperCase()
  if (c === 'EXCELLENT') return 'ufpm-cat-tag--excellent'
  if (c === 'SATISFACTORY') return 'ufpm-cat-tag--satisfactory'
  return 'ufpm-cat-tag--unsatisfactory'
}

function setTodayRange(): void {
  const today = todayISO()
  dateFrom.value = today
  dateTo.value = today
}

function exportExcel(): void {
  exportHistoryXlsx(
    filteredEntries.value,
    members.value,
    dateFrom.value.trim(),
    dateTo.value.trim(),
  )
}
</script>

<template>
  <div class="card">
    <div class="card-title ufpm-history-title-row">
      <span>Unit Test History</span>
      <button
        class="btn btn-secondary ufpm-history-export-btn"
        type="button"
        :disabled="!filteredEntries.length"
        @click="exportExcel"
      >Export Excel</button>
    </div>

    <div class="ufpm-history-filters">
      <label class="ufpm-history-filter-label">
        From
        <div class="ufpm-history-filter-row">
          <input v-model="dateFrom" type="date" class="ufpm-history-date-input" aria-label="Filter from date" />
        </div>
      </label>

      <label class="ufpm-history-filter-label">
        To
        <div class="ufpm-history-filter-row">
          <input v-model="dateTo" type="date" class="ufpm-history-date-input" aria-label="Filter to date" />
        </div>
      </label>

      <div class="ufpm-history-filter-actions">
        <button
          class="btn btn-secondary"
          type="button"
          @click="setTodayRange"
        >Today</button>
        <button
          class="btn btn-secondary"
          type="button"
          @click="clearDateFilters"
        >All Dates</button>
      </div>

      <label class="ufpm-history-filter-label ufpm-history-filter-label--member">
        Member
        <select v-model="memberFilter" class="ufpm-history-member-select" aria-label="Filter by member">
          <option value="">All Members</option>
          <option v-for="opt in memberOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
        </select>
      </label>
    </div>

    <p class="ufpm-history-export-hint">
      Export uses roster-style columns plus component completion, scores, and raw results for the filtered date range.
    </p>

    <div v-if="filteredEntries.length" class="ufpm-history-list">
      <div v-for="entry in filteredEntries" :key="String(entry.id)" class="ufpm-hcl-item">
        <div class="ufpm-hcl-header">
          <span class="ufpm-hcl-score" :class="scoreBandClass(entry.cat, entry.composite)">{{ compositeLabel(entry.composite) }}</span>
          <span class="tag ufpm-cat-tag" :class="scoreClass(entry.cat)">{{ entry.cat || '—' }}</span>
          <span class="ufpm-hcl-date">{{ entry.recordedDate || entry.date || '—' }}</span>
          <span class="ufpm-hcl-member">{{ entry.memberName }}</span>
        </div>
        <div class="ufpm-hcl-breakdown">
          PU: {{ scoreLabel(entry.puScore, entry.puEx) }} ·
          Core: {{ scoreLabel(entry.coreScore, entry.coreEx) }} ·
          Cardio: {{ scoreLabel(entry.cardioScore, entry.cardioEx) }} ·
          WHtR: {{ scoreLabel(entry.whtrScore, entry.whtrEx) }}
        </div>
      </div>
    </div>
    <div v-else class="ufpm-empty" style="margin-top: 12px;">
      <template v-if="!memberOptions.length">
        No roster imported. Import a CSV in the Roster tab to track unit history.
      </template>
      <template v-else>
        No test results for the selected filters.
      </template>
    </div>
  </div>
</template>